/* ============================================================
 * PRINTING FROM THE MOBILE APPS — the PDF + share half, for EVERY print surface
 * ------------------------------------------------------------
 * WAS `brokerPdf.js`, RENAMED 2026-09-11. It shipped serving one surface and now
 * serves four, and a file the asset-label printer imports must not be named
 * after the broker modal. Nothing in it was broker-specific except the filename
 * helper, which is now `pdfFilename(prefix, name, stamp)`.
 *
 * WHY THIS FILE EXISTS. Four buttons called `window.print()` and nothing else.
 * On the website that opens the print dialog; inside the app it does NOTHING AT
 * ALL, because `window.print` is undefined in both WebViews — WKWebView never
 * implemented it (Safari did), and Android WebView leaves printing to the native
 * PrintManager. No error, no dialog, no log line: the owner pressed a button and
 * the app sat there. Four times over.
 *
 * WHAT THE APP GETS INSTEAD. The SAME DOM the website prints is rasterised with
 * html2canvas, wrapped in a PDF with jsPDF, written to the app's cache directory
 * and handed to the native share sheet — so the owner saves it or sends it on
 * WhatsApp from the OS, which is what they asked for.
 *
 * THE FOUR SURFACES, and they are NOT the same document shape — which is why
 * `elementToPdfDataUri` takes a layout and a format rather than assuming A4-fit:
 *
 *   | surface                | node                  | format      | layout    |
 *   |------------------------|-----------------------|-------------|-----------|
 *   | Broker Calculation     | #broker-print-root    | a4 portrait | fit       |
 *   | Reports › Export PDF   | the Reports page root | a4 portrait | paginate  |
 *   | Commission Payout      | #payout-print-area    | a4 portrait | paginate  |
 *   | Asset labels           | an off-screen label   | [w,h] mm    | repeat    |
 *
 * `fit` scales one screenful onto one page; `paginate` slices a tall document
 * across as many pages as it needs; `repeat` emits the same page N times, which
 * is what a run of thermal labels is. Forcing any of the three into another's
 * shape produces a file that opens and is wrong — a shrunk-to-illegible report,
 * or a 40x30mm label centred on A4.
 *
 * RASTERISED, NOT REBUILT, AND THAT IS THE POINT. The alternative was to draw
 * the document again with PDF text primitives. It would be smaller and the text
 * would be selectable, and it would also be a SECOND implementation of a
 * document whose numbers are commission figures — free to drift from the one the
 * website prints, in a place nobody would look. html2canvas photographs the
 * element that is already there, so "same layout, same numbers, same date line"
 * is true by construction rather than by review. The date line in particular is
 * never re-derived here: this file never formats a date.
 *
 * ------------------------------------------------------------
 * THIS NEEDS A NATIVE RELEASE, AND THAT IS NOT A BUG IN IT.
 *
 * capacitor.config.ts sets `server.url = https://crm-aro.com`, so the app's
 * WebView loads the LIVE SITE: every JS change reaches every installed app the
 * moment Vercel deploys. Native PLUGINS do not work that way — they are compiled
 * into the binary. The installed build carries five (biometric-auth,
 * privacy-screen, firebase-messaging, geolocation, local-notifications) and
 * neither Share nor Filesystem is among them.
 *
 * So until a new build ships to the App Store and Play Store,
 * `Capacitor.isPluginAvailable` answers false on the device and the caller shows
 * an honest message naming the update. That is deliberate: the failure this
 * replaces was SILENCE, and "nothing happened" is the one outcome that must not
 * survive. `pdfShareSupport()` is the single place that decides which of the
 * three worlds we are in, and it is pure enough for a build gate to execute.
 * ============================================================ */

import { registerPlugin } from "@capacitor/core";

// DIRECT NATIVE-BRIDGE PROXIES, the pattern biometric.js and geolocation.js
// already use here and for the reason written on those files: a plugin's own JS
// index can do a nested lazy `import('./native')`, and that chunk never resolves
// when the app is served from a remote server.url origin — the call hangs
// forever with no error. Talking to the plugin BY NAME has no secondary chunk.
// The npm packages are still required: they ship the native code and the
// capacitor.plugins.json / SPM registration.
var ShareNative = registerPlugin("Share");
var FilesystemNative = registerPlugin("Filesystem");

// The Capacitor `Directory.Cache` value, inlined rather than imported from
// @capacitor/filesystem — importing the enum pulls in that package's JS index,
// which is the nested-chunk hazard the proxies above exist to avoid. It is a
// stable string in the plugin's public API.
var CACHE_DIR = "CACHE";

// A4 portrait at 96 CSS px/inch. The off-screen clone is laid out at this width
// so the rasterised page has the same line breaks as the printed one.
export var A4_WIDTH_PX = 794;

function isNativePlatformSync() {
  try {
    return !!(typeof window !== "undefined" && window.Capacitor
      && typeof window.Capacitor.isNativePlatform === "function"
      && window.Capacitor.isNativePlatform());
  } catch (e) { return false; }
}

function pluginAvailable(name) {
  try {
    return !!(typeof window !== "undefined" && window.Capacitor
      && typeof window.Capacitor.isPluginAvailable === "function"
      && window.Capacitor.isPluginAvailable(name));
  } catch (e) { return false; }
}

/* WHICH OF THREE WORLDS ARE WE IN — the whole branch, in one pure-ish function
 * at module scope so verify-broker-print.js can execute it over every
 * combination instead of describing it.
 *
 *   "print"   a browser with window.print  -> the website, unchanged behaviour
 *   "share"   native AND both plugins present -> PDF + native share sheet
 *   "stale"   native and a plugin missing  -> the binary predates this feature;
 *                                            the caller must SAY SO
 *   "none"    neither                      -> no path; the caller must say so
 *
 * `env` is injectable ONLY so the gate can run it; every real caller passes
 * nothing and it reads the live globals.
 */
export function pdfShareSupport(env) {
  var e = env || {};
  var native = (e.native !== undefined) ? !!e.native : isNativePlatformSync();
  var hasShare = (e.hasShare !== undefined) ? !!e.hasShare : pluginAvailable("Share");
  var hasFs = (e.hasFilesystem !== undefined) ? !!e.hasFilesystem : pluginAvailable("Filesystem");
  var canPrint = (e.canPrint !== undefined)
    ? !!e.canPrint
    : (typeof window !== "undefined" && typeof window.print === "function");

  // NATIVE IS CHECKED FIRST, DELIBERATELY. An app WebView can report a
  // `window.print` that exists and does nothing (Android has shipped both
  // shapes), so trusting `canPrint` first would send the app down the path this
  // file exists to replace and produce exactly the original silent failure.
  if (native) return (hasShare && hasFs) ? "share" : "stale";
  if (canPrint) return "print";
  return "none";
}

/* THE MESSAGE A SURFACE SHOWS WHEN IT CANNOT PRODUCE A PDF — one copy, four
 * surfaces. Keyed by the `pdfShareSupport()` outcome that cannot be served.
 *
 * "stale" IS THE NORMAL CASE UNTIL THE NEXT STORE RELEASE, not an edge: the
 * WebView loads the live site, so this JS is on every installed phone while the
 * Share/Filesystem plugins are not. Four surfaces saying it four slightly
 * different ways is how a support answer becomes four support answers.
 *
 * The failure all of this replaces was SILENCE. "Nothing happened" is the one
 * outcome that must not survive, on any surface, in any branch.
 */
export var PRINT_UNSUPPORTED_MSG = {
  stale: "Update the ARO CRM app from the App Store / Play Store to save or send this as a PDF. Printing from the website works now.",
  none:  "This device cannot print or share. Open the CRM in a browser to print."
};

// The saved file's name, for any surface. Kept here, beside the share call that
// uses it, and deliberately NOT given the document's own date: a report or a
// broker sheet prints its period or its date INSIDE the page, and that is the
// authority. A second date in the filename is a second copy of that fact which
// can disagree with it. The timestamp below is when the FILE was made, which is
// a different thing and is only there to stop two shares overwriting each other
// in the cache.
export function pdfFilename(prefix, name, stamp) {
  var safe = String(name == null ? "" : name)
    .replace(/[^\p{L}\p{N}]+/gu, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 60);
  var pre = String(prefix || "print").replace(/[^A-Za-z0-9-]+/g, "") || "print";
  return pre + "-" + (safe || "document") + "-" + String(stamp || Date.now()) + ".pdf";
}

// Strip the `data:application/pdf;base64,` prefix jsPDF's datauristring output
// carries. Filesystem.writeFile wants the base64 payload alone, and a prefix
// left on it writes a file that opens as garbage rather than failing — the kind
// of wrong that looks like a PDF bug.
export function base64Payload(dataUri) {
  var s = String(dataUri == null ? "" : dataUri);
  var comma = s.indexOf(",");
  return (s.slice(0, 5) === "data:" && comma >= 0) ? s.slice(comma + 1) : s;
}

/* Rasterise `el` and return a PDF as a base64 data URI.
 *
 * opts:
 *   layout      "fit" (default) | "paginate" | "repeat"
 *   format      "a4" (default) | [widthMm, heightMm]
 *   orientation "portrait" (default) | "landscape"
 *   marginPt    page margin in points (default 24; 0 for labels)
 *   repeat      page count for layout "repeat" (default 1)
 *   scale       html2canvas scale (default 2)
 *   widthPx     the CSS width the node was laid out at (for windowWidth)
 *   cloneSwaps  [{selector, display}] applied to the CLONE only — see below
 *
 * CLONE SWAPS EXIST BECAUSE A PRINT STYLESHEET IS NOT IN EFFECT ON SCREEN.
 * The payout report hides its interactive chrome with `.no-print` and reveals a
 * header with `.print-only`, both inside `@media print`. On screen those rules
 * are off, so a naive rasterise photographs a DIFFERENT document from the one
 * the browser prints — chrome in, header out. html2canvas's `onclone` hands us
 * the cloned DOM, so the swaps are applied there: the real page never flickers,
 * and the swap list is declared ONCE by the call site instead of being a second
 * copy of the CSS living in a screen-media class.
 *
 * The libraries are loaded with dynamic import so neither reaches the main
 * bundle: nothing downloads until a print button is pressed. Top-level lazy
 * chunks do load from the remote server.url origin — pushNotifications.js does
 * the same with @capacitor/core. It is a plugin's own NESTED import that hangs,
 * which is why the two plugins above are bridged by name instead.
 */
export async function elementToPdfDataUri(el, opts) {
  if (!el) throw new Error("nothing to print");
  var o = opts || {};
  var layout = o.layout || "fit";
  var h2cMod = await import("html2canvas");
  var jsPdfMod = await import("jspdf");
  var html2canvas = h2cMod.default || h2cMod;
  var jsPDF = jsPdfMod.jsPDF || jsPdfMod.default || jsPdfMod;

  var swaps = o.cloneSwaps || [];
  var canvas = await html2canvas(el, {
    scale: o.scale || 2,               // legible on a phone screen and when zoomed
    backgroundColor: "#ffffff",
    useCORS: true,
    logging: false,
    windowWidth: o.widthPx || A4_WIDTH_PX,
    onclone: function (doc) {
      swaps.forEach(function (sw) {
        var nodes = doc.querySelectorAll(sw.selector);
        for (var i = 0; i < nodes.length; i++) nodes[i].style.display = sw.display;
      });
    }
  });
  if (!canvas || !canvas.width || !canvas.height) throw new Error("could not render the page");

  // COMPRESSION IS NOT A NICETY HERE — MEASURED, headless Chrome, the broker
  // document at scale 2 (1588x814):
  //     PNG, no compression   3,881,217 bytes    <- jsPDF's default
  //     PNG, compress + FAST     91,059 bytes
  //     PNG, compress + SLOW     90,786 bytes
  //     JPEG, compress + FAST   103,170 bytes
  // A 3.9 MB attachment for a seven-row sheet is the difference between "send on
  // WhatsApp" working and the owner giving up on it, and the default is the big
  // one. PNG beats JPEG on a flat-colour document AND is lossless, so the text
  // stays crisp; SLOW buys 273 bytes for real CPU on a phone, so FAST it is.
  //
  // `compress: true` (the deflate stream) is doing almost all of the work — the
  // addImage compression argument alone does not reach the page content stream.
  // Both are set, deliberately: dropping either silently restores a multi-MB
  // file, which is why verify-broker-print.js pins them. It matters MORE for the
  // paginated surfaces, where the same image is sliced across N pages.
  // FORMAT IS IN THE DOCUMENT'S UNIT, AND THIS COST A BUG. jsPDF reads a
  // `format` array in whatever `unit` the document was opened with — pt here —
  // so passing a label's [40, 30] MILLIMETRES produced a 40pt x 30pt page:
  // 14mm x 10.6mm, a third of the intended size. `formatMm` converts, so a
  // caller states millimetres and gets millimetres.
  //
  // ORIENTATION IS DERIVED FOR A CUSTOM PAGE, never defaulted. jsPDF NORMALISES
  // the format to match the orientation, so `orientation: "portrait"` silently
  // swapped a 40x30 landscape label to 30x40 portrait. Measured: the rendered
  // page came out 0.75 aspect against the label's 1.333. A4 keeps its explicit
  // default because a report is portrait by intent, not by its dimensions.
  var MM_TO_PT = 72 / 25.4;
  var fmt = o.format || "a4";
  var orient = o.orientation || "portrait";
  if (o.formatMm && o.formatMm.length === 2) {
    fmt = [o.formatMm[0] * MM_TO_PT, o.formatMm[1] * MM_TO_PT];
    orient = (o.formatMm[0] >= o.formatMm[1]) ? "landscape" : "portrait";
  }
  var pdf = new jsPDF({ unit: "pt", format: fmt, orientation: orient, compress: true });
  var pageW = pdf.internal.pageSize.getWidth();
  var pageH = pdf.internal.pageSize.getHeight();
  var margin = (o.marginPt != null) ? o.marginPt : 24;
  var maxW = pageW - margin * 2;
  var maxH = pageH - margin * 2;
  var full = canvas.toDataURL("image/png");

  if (layout === "repeat") {
    // N COPIES OF ONE PAGE. A run of thermal labels is not a tall document — it
    // is the same label again, and the printer advances between them. Fitted to
    // the page like "fit", then emitted `repeat` times.
    var rN = Math.max(1, Math.min(99, parseInt(o.repeat, 10) || 1));
    var rRatio = Math.min(maxW / canvas.width, maxH / canvas.height);
    var rW = canvas.width * rRatio, rH = canvas.height * rRatio;
    for (var c = 0; c < rN; c++) {
      if (c > 0) pdf.addPage(fmt, orient);
      pdf.addImage(full, "PNG", margin + (maxW - rW) / 2, margin + (maxH - rH) / 2, rW, rH, undefined, "FAST");
    }
    return pdf.output("datauristring");
  }

  if (layout === "paginate") {
    // A TALL DOCUMENT IS SLICED, NEVER SHRUNK. A month of payouts or a full
    // report is metres long; fitting it to one page produces a file that opens
    // and cannot be read, which is the worst kind of wrong because it looks like
    // it worked.
    //
    // SLICED ON A TEMP CANVAS, not by drawing the same image N times at negative
    // offsets. The offset form leaves the parts above and below the page on the
    // page's content stream — every page carries the WHOLE image, so an 8-page
    // report embeds it 8 times and the file is 8x too big. MEASURED: a 3,791px
    // report is 1,095,813 bytes across 4 sliced pages against 1,092,813 for the
    // same content fitted onto ONE page — i.e. slicing costs 3 KB, not 3x.
    //
    // SIZE AT REALISTIC LENGTH, measured on a 9,371px payout report (a long
    // month), all with compress:true:
    //     scale 2,   PNG   9 pages  2,801,385 bytes  (304 KB/page)   <- shipped
    //     scale 2,   JPEG  9 pages  2,949,507 bytes  (320 KB/page)
    //     scale 1.5, PNG   9 pages  2,007,651 bytes  (218 KB/page)
    //     scale 1.5, JPEG  9 pages  2,042,199 bytes  (222 KB/page)
    // PNG beats JPEG at BOTH scales — this is text on white, not a photograph.
    // scale 2 is kept because ~209 DPI is what makes a report readable on a
    // phone, and 2.8 MB is well inside WhatsApp's document limit. **scale 1.5 is
    // the lever** if a report ever gets long enough to matter: 28% smaller, one
    // option away, and the reason it is written down rather than guessed at.
    var scaleToPage = maxW / canvas.width;               // width always fits the page
    var sliceH = Math.max(1, Math.floor(maxH / scaleToPage));  // canvas px per page
    var pages = Math.max(1, Math.ceil(canvas.height / sliceH));
    for (var i = 0; i < pages; i++) {
      var y = i * sliceH;
      var h = Math.min(sliceH, canvas.height - y);
      var tmp = document.createElement("canvas");
      tmp.width = canvas.width;
      tmp.height = h;
      var ctx = tmp.getContext("2d");
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, tmp.width, tmp.height);
      ctx.drawImage(canvas, 0, y, canvas.width, h, 0, 0, canvas.width, h);
      if (i > 0) pdf.addPage(fmt, orient);
      pdf.addImage(tmp.toDataURL("image/png"), "PNG", margin, margin,
                   canvas.width * scaleToPage, h * scaleToPage, undefined, "FAST");
    }
    return pdf.output("datauristring");
  }

  // "fit" — one screenful, scaled onto one page. FIT, NEVER SLICE: the broker
  // copy is explicitly one page (that is what the display-based print stylesheet
  // in App.js was built to guarantee), so a second page is a difference from
  // what the website prints.
  var ratio = Math.min(maxW / canvas.width, maxH / canvas.height);
  pdf.addImage(full, "PNG", margin, margin, canvas.width * ratio, canvas.height * ratio, undefined, "FAST");
  return pdf.output("datauristring");
}

/* Write the PDF into the app cache and open the native share sheet.
 *
 * WRITTEN TO A FILE FIRST, because that is what the share sheet can carry. A
 * `url:` share hands over a link and a `text:` share hands over a string;
 * neither gives the owner a PDF to forward on WhatsApp or to save. `files:` is
 * the only shape that does, and it takes a native URI — hence Filesystem.
 *
 * Cache, not Documents: this is a throwaway copy of a thing the owner is about
 * to send. The OS reclaims Cache on its own, so the app does not accumulate
 * every broker sheet ever printed, and nothing here needs to survive a restart.
 */
export async function sharePdfDataUri(dataUri, filename, title) {
  var name = String(filename || "broker.pdf");
  var written = await FilesystemNative.writeFile({
    path: name,
    data: base64Payload(dataUri),
    directory: CACHE_DIR,
    recursive: true
  });
  var uri = written && written.uri;
  if (!uri) throw new Error("could not save the PDF");
  await ShareNative.share({
    title: title || "Broker Calculation",
    files: [uri],
    dialogTitle: title || "Broker Calculation"
  });
  return uri;
}

/* ===== THE ONE DELIVERY PATH — every print surface calls THIS =============
 *
 * Four surfaces needed the same six steps, and four copies of them is four
 * chances to get one wrong in a way only that surface shows. Everything that
 * was learned the hard way lives here once:
 *
 *   1. html2canvas CANNOT PHOTOGRAPH `display:none`. Three of the four nodes are
 *      hidden outside `@media print`, so the node is laid out for REAL and moved
 *      off-screen instead of hidden. A hidden node yields a blank canvas — a
 *      failure that still produces a file, which is the hardest kind to notice.
 *   2. Laid out at an explicit width, so the rasterised line breaks match the
 *      printed ones rather than the phone's viewport.
 *   3. TWO frames, not one: the first commits the style writes, the second lets
 *      the browser lay the node out at its new width. Rasterising mid-layout
 *      gives a clipped page.
 *   4. The node's own inline styles are RESTORED afterwards, whatever happened.
 *      `#payout-print-area` and the Reports root are real, permanently mounted
 *      parts of the page — leaving them parked at -10000px would blank the
 *      screen the owner is looking at. (The broker root is disposable and is
 *      removed by its own cleanup; restoring it too is harmless.)
 *   5. A CANCELLED SHARE IS NOT AN ERROR. Both platforms reject the share
 *      promise when the user dismisses the sheet; reporting that as "failed"
 *      trains the owner to ignore the message that matters. Resolves `false`
 *      for a dismissal and `true` for a completed share, so a caller can tell
 *      them apart without parsing a message.
 *   6. It THROWS on a real failure, so every caller's `.catch` is the one place
 *      that surface reports from.
 */
export async function captureAndShare(el, opts) {
  if (!el) throw new Error("nothing to print");
  var o = opts || {};
  var widthPx = o.widthPx || A4_WIDTH_PX;
  var prev = {
    display: el.style.display, position: el.style.position, left: el.style.left,
    top: el.style.top, width: el.style.width, background: el.style.background,
    zIndex: el.style.zIndex, maxHeight: el.style.maxHeight, overflow: el.style.overflow
  };
  var restore = function () {
    el.style.display = prev.display; el.style.position = prev.position;
    el.style.left = prev.left; el.style.top = prev.top; el.style.width = prev.width;
    el.style.background = prev.background; el.style.zIndex = prev.zIndex;
    el.style.maxHeight = prev.maxHeight; el.style.overflow = prev.overflow;
  };
  try {
    el.style.display = "block";
    el.style.position = "fixed";
    el.style.left = "-10000px";
    el.style.top = "0";
    el.style.width = widthPx + "px";
    el.style.background = "#fff";
    el.style.zIndex = "-1";
    // A scroll container would be photographed at its clipped height, so the
    // paginated surfaces must be allowed to take their full height.
    el.style.maxHeight = "none";
    el.style.overflow = "visible";
    await new Promise(function (r) {
      if (typeof requestAnimationFrame !== "function") { setTimeout(r, 80); return; }
      requestAnimationFrame(function () { requestAnimationFrame(r); });
    });
    var uri = await elementToPdfDataUri(el, Object.assign({ widthPx: widthPx }, o));
    restore();
    await sharePdfDataUri(uri, o.filename, o.title);
    return true;
  } catch (ex) {
    restore();
    var m = String((ex && ex.message) || "");
    if (/cancel/i.test(m) || /dismiss/i.test(m) || /abort/i.test(m)) return false;
    throw ex;
  }
}
